import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getFXRates, updateFXRates, updateFXRatesRange } from '../utils/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ArrowPathIcon, CurrencyDollarIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import Modal from '../components/Modal';

const FXRates = () => {
  usePageTitle('Exchange Rates');

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [searchCurrency, setSearchCurrency] = useState('');
  const [actualDate, setActualDate] = useState(null); // The actual date of the rates being shown

  // Bulk update states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    loadRates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadRates = async () => {
    setLoading(true);
    setMessage('');
    setActualDate(null);
    try {
      const response = await getFXRates(selectedDate);
      const fetchedRates = response.data.rates || [];
      setRates(fetchedRates);

      // Check if backend explicitly provided actualDate (rates from different date)
      if (response.data.actualDate && response.data.actualDate !== selectedDate) {
        setActualDate(response.data.actualDate);
        setMessage(response.data.message);
      } else if (fetchedRates.length > 0) {
        // Fallback: check if rates' valid_for differs from selected date (convert UTC to local date)
        const rateDateObj = new Date(fetchedRates[0].valid_for);
        const year = rateDateObj.getFullYear();
        const month = String(rateDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(rateDateObj.getDate()).padStart(2, '0');
        const rateDate = `${year}-${month}-${day}`;

        if (rateDate !== selectedDate) {
          setActualDate(rateDate);
          setMessage(response.data.message || `Showing last available rates from ${format(new Date(rateDate), 'MMMM d, yyyy')}`);
        }
      } else if (response.data.message) {
        setMessage(response.data.message);
      }
    } catch (error) {
      toast.error('Error loading FX rates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRates = async () => {
    setUpdating(true);
    try {
      const response = await updateFXRates();
      toast.success(response.data.message || 'FX rates updated successfully');
      // Reload if selected date is today
      const today = new Date().toISOString().split('T')[0];
      if (selectedDate === today) {
        loadRates();
      }
    } catch (error) {
      toast.error('Error updating FX rates');
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both from and to dates');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error('From date must be before or equal to To date');
      return;
    }

    setBulkUpdating(true);
    setBulkResults(null);

    // Calculate total days for progress tracking
    const diffTime = Math.abs(new Date(toDate) - new Date(fromDate));
    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    setProgress({ current: 0, total: totalDays, percent: 0 });

    try {
      const response = await updateFXRatesRange(fromDate, toDate);
      setBulkResults(response.data);
      setProgress({ current: totalDays, total: totalDays, percent: 100 });
      toast.success(response.data.message || 'Bulk update completed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error updating FX rates');
      console.error(error);
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleCloseBulkModal = () => {
    if (!bulkUpdating) {
      setShowBulkModal(false);
      setFromDate('');
      setToDate('');
      setBulkResults(null);
      setProgress({ current: 0, total: 0, percent: 0 });
    }
  };

  const formatRate = (rate) => {
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(rate);
  };

  // Filter rates by currency code search
  const filteredRates = rates.filter(rate =>
    searchCurrency === '' ||
    rate.currency_code.toLowerCase().includes(searchCurrency.toLowerCase()) ||
    rate.country.toLowerCase().includes(searchCurrency.toLowerCase()) ||
    rate.currency.toLowerCase().includes(searchCurrency.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Foreign Exchange Rates</h1>
          <p className="page-subtitle">Exchange rates from Czech National Bank</p>
        </div>
        <div className="flex gap-md">
          <button
            className="btn btn-secondary"
            onClick={() => setShowBulkModal(true)}
          >
            <CalendarDaysIcon style={{ width: 18, height: 18 }} />
            Update Period
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdateRates}
            disabled={updating}
          >
            <ArrowPathIcon style={{ width: 18, height: 18 }} />
            {updating ? 'Updating...' : 'Update Today\'s Rates'}
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ marginBottom: 'var(--space-lg)', display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className="form-label">Select Date</label>
            <input
              type="date"
              className="form-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={{ width: '200px' }}
            />
          </div>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <label className="form-label">Search Currency</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by currency code, country, or currency name..."
              value={searchCurrency}
              onChange={(e) => setSearchCurrency(e.target.value)}
            />
          </div>
        </div>

        {actualDate && (
          <div
            style={{
              marginBottom: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)' }}>
              <CurrencyDollarIcon style={{ width: 18, height: 18, color: 'var(--color-primary)' }} />
              <strong style={{ fontSize: '0.9375rem' }}>Last Available Exchange Rates</strong>
            </div>
            <div style={{ color: 'var(--color-text-secondary)', marginLeft: '26px' }}>
              No rates available for <strong>{format(new Date(selectedDate), 'MMMM d, yyyy')}</strong>.
              <br />
              Showing rates from: <strong style={{ color: 'var(--color-primary)' }}>{format(new Date(actualDate), 'MMMM d, yyyy')}</strong>
            </div>
          </div>
        )}

        {message && !actualDate && (
          <div
            style={{
              marginBottom: 'var(--space-lg)',
              padding: 'var(--space-sm)',
              background: 'var(--color-warning-bg)',
              color: 'var(--color-warning)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem'
            }}
          >
            {message}
          </div>
        )}

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading rates...</span>
          </div>
        ) : filteredRates.length > 0 ? (
          <>
            {searchCurrency && (
              <div style={{ marginBottom: 'var(--space-md)', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Showing {filteredRates.length} of {rates.length} currencies
              </div>
            )}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Currency Code</th>
                    <th>Country</th>
                    <th>Currency</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Rate (CZK)</th>
                    <th style={{ textAlign: 'right' }}>Per 1 Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRates.map((rate, index) => (
                    <tr key={index}>
                      <td>
                        <span className="badge badge-draft">{rate.currency_code}</span>
                      </td>
                      <td>{rate.country}</td>
                      <td>{rate.currency}</td>
                      <td style={{ textAlign: 'right' }} className="font-mono">
                        {rate.amount}
                      </td>
                      <td style={{ textAlign: 'right' }} className="font-mono">
                        {formatRate(rate.rate)}
                      </td>
                      <td style={{ textAlign: 'right' }} className="font-mono">
                        {formatRate(rate.rate / rate.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : rates.length > 0 && searchCurrency ? (
          <div className="empty-state">
            <CurrencyDollarIcon className="empty-state-icon" />
            <p className="empty-state-title">No matching currencies</p>
            <p>No currencies found matching "{searchCurrency}"</p>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setSearchCurrency('')}
              style={{ marginTop: 'var(--space-md)' }}
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="empty-state">
            <CurrencyDollarIcon className="empty-state-icon" />
            <p className="empty-state-title">No rates available</p>
            <p>No foreign exchange rates found for {format(new Date(selectedDate), 'MMMM d, yyyy')}</p>
          </div>
        )}
      </div>

      {rates.length > 0 && (
        <div
          style={{
            marginTop: 'var(--space-md)',
            padding: 'var(--space-md)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)'
          }}
        >
          <strong>Note:</strong> Exchange rates are published by the Czech National Bank
          every working day after 2:30 PM and are valid for the current working day.
          The rates show how many Czech korunas (CZK) equal the specified amount of foreign currency.
        </div>
      )}

      {/* Bulk Update Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={handleCloseBulkModal}
        title="Update FX Rates for Period"
      >
        <div style={{ padding: 'var(--space-lg)' }}>
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              disabled={bulkUpdating}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              disabled={bulkUpdating}
            />
          </div>

          {bulkUpdating && (
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{ marginBottom: 'var(--space-sm)', display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span>Processing...</span>
                <span>{progress.percent.toFixed(0)}%</span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--color-bg-tertiary)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${progress.percent}%`,
                    height: '100%',
                    background: 'var(--color-primary)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
          )}

          {bulkResults && (
            <div
              style={{
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem'
              }}
            >
              <h4 style={{ marginBottom: 'var(--space-sm)', fontWeight: 600 }}>Results Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-sm)' }}>
                <div>Total Days: <strong>{bulkResults.summary.total}</strong></div>
                <div style={{ color: 'var(--color-success)' }}>Success: <strong>{bulkResults.summary.success}</strong></div>
                <div style={{ color: 'var(--color-warning)' }}>Skipped: <strong>{bulkResults.summary.skipped}</strong></div>
                <div style={{ color: 'var(--color-error)' }}>Errors: <strong>{bulkResults.summary.errors}</strong></div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-ghost"
              onClick={handleCloseBulkModal}
              disabled={bulkUpdating}
            >
              {bulkResults ? 'Close' : 'Cancel'}
            </button>
            {!bulkResults && (
              <button
                className="btn btn-primary"
                onClick={handleBulkUpdate}
                disabled={bulkUpdating}
              >
                {bulkUpdating ? 'Updating...' : 'Update'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FXRates;
